import type { ReactNode } from 'react'

export const COLORS = {
  frontend: { stroke: '#7c3aed', bg: '#ede9fe', text: '#3b0764', grad: ['#f5f3ff', '#ede9fe'] },
  api: { stroke: '#e11d48', bg: '#ffe4e6', text: '#881337', grad: ['#fff1f2', '#ffe4e6'] },
  mock: { stroke: '#64748b', bg: '#f1f5f9', text: '#1e293b', grad: ['#f8fafc', '#e2e8f0'] },
  mllp: { stroke: '#0891b2', bg: '#cffafe', text: '#083344', grad: ['#ecfeff', '#cffafe'] },
  db: { stroke: '#2563eb', bg: '#dbeafe', text: '#1e3a8a', grad: ['#eff6ff', '#dbeafe'] },
  partner: { stroke: '#16a34a', bg: '#dcfce7', text: '#14532d', grad: ['#f0fdf4', '#dcfce7'] },
  ci: { stroke: '#4338ca', bg: '#e0e7ff', text: '#312e81', grad: ['#eef2ff', '#e0e7ff'] },
  auth: { stroke: '#a21caf', bg: '#fae8ff', text: '#4a044e', grad: ['#fdf4ff', '#fae8ff'] },
  network: { stroke: '#b45309', bg: '#fef3c7', text: '#78350f', grad: ['#fffbeb', '#fef3c7'] },
  neutral: { stroke: '#94a3b8', bg: '#f1f5f9', text: '#334155', grad: ['#f8fafc', '#f1f5f9'] },
  danger: { stroke: '#dc2626', bg: '#fee2e2', text: '#7f1d1d', grad: ['#fef2f2', '#fee2e2'] },
  success: { stroke: '#16a34a', bg: '#dcfce7', text: '#14532d', grad: ['#f0fdf4', '#dcfce7'] },
} as const

export type ColorKey = keyof typeof COLORS

const F = 'system-ui, -apple-system, sans-serif'

export function SvgDefs({ id }: { id: string }) {
  return (
    <defs>
      {Object.entries(COLORS).map(([key, val]) => (
        <linearGradient key={key} id={`${id}-${key}`} x1='0' y1='0' x2='0' y2='1'>
          <stop offset='0%' stopColor={val.grad[0]} />
          <stop offset='100%' stopColor={val.grad[1]} />
        </linearGradient>
      ))}
      <filter id={`${id}-sh`} x='-8%' y='-8%' width='120%' height='135%'>
        <feDropShadow dx='0' dy='2' stdDeviation='4' floodColor='#000' floodOpacity='.06' />
      </filter>
      <filter id={`${id}-sh-sm`} x='-4%' y='-4%' width='110%' height='118%'>
        <feDropShadow dx='0' dy='1' stdDeviation='2' floodColor='#000' floodOpacity='.04' />
      </filter>
      <marker id={`${id}-arr`} viewBox='0 0 10 10' refX='9' refY='5' markerWidth='6' markerHeight='6' orient='auto-start-reverse'>
        <path d='M 0 0 L 10 5 L 0 10 z' fill='#94a3b8' />
      </marker>
      <marker id={`${id}-arr-d`} viewBox='0 0 10 10' refX='9' refY='5' markerWidth='6' markerHeight='6' orient='auto-start-reverse'>
        <path d='M 0 0 L 10 5 L 0 10 z' fill='#64748b' />
      </marker>
    </defs>
  )
}

interface BoxProps {
  x: number; y: number; w?: number; h?: number
  title: string; subtitle?: string
  color: ColorKey; id: string
}

export function Box({ x, y, w = 200, h = 76, title, subtitle, color, id }: BoxProps) {
  const c = COLORS[color]
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={10} ry={10}
        fill={`url(#${id}-${color})`} stroke={c.stroke} strokeWidth={1.5} filter={`url(#${id}-sh)`} />
      <text x={x + w / 2} y={y + (subtitle ? h / 2 - 7 : h / 2 + 1)}
        textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={13} fontWeight={600} fill={c.text}>{title}</text>
      {subtitle && (
        <text x={x + w / 2} y={y + h / 2 + 12}
          textAnchor='middle' dominantBaseline='middle'
          fontFamily={F} fontSize={10} fill={c.text} opacity={0.6}>{subtitle}</text>
      )}
    </g>
  )
}

interface CylProps {
  x: number; y: number; w?: number; h?: number
  title: string; subtitle?: string
  color: ColorKey; id: string
}

export function Cyl({ x, y, w = 160, h = 80, title, subtitle, color, id }: CylProps) {
  const c = COLORS[color]
  const e = 12
  return (
    <g filter={`url(#${id}-sh)`}>
      <path d={`M${x} ${y + e} Q${x} ${y},${x + w / 2} ${y} Q${x + w} ${y},${x + w} ${y + e} L${x + w} ${y + h - e} Q${x + w} ${y + h},${x + w / 2} ${y + h} Q${x} ${y + h},${x} ${y + h - e}Z`}
        fill={`url(#${id}-${color})`} stroke={c.stroke} strokeWidth={1.5} />
      <path d={`M${x} ${y + e} Q${x} ${y + e * 2},${x + w / 2} ${y + e * 2} Q${x + w} ${y + e * 2},${x + w} ${y + e}`}
        fill='none' stroke={c.stroke} strokeWidth={1} opacity={0.25} />
      <text x={x + w / 2} y={y + h / 2 + (subtitle ? -3 : 5)}
        textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={13} fontWeight={600} fill={c.text}>{title}</text>
      {subtitle && (
        <text x={x + w / 2} y={y + h / 2 + 15}
          textAnchor='middle' dominantBaseline='middle'
          fontFamily={F} fontSize={10} fill={c.text} opacity={0.6}>{subtitle}</text>
      )}
    </g>
  )
}

interface PillProps {
  x: number; y: number; w?: number; h?: number
  title: string; subtitle?: string
  color: ColorKey; id: string
}

export function Pill({ x, y, w = 230, h = 56, title, subtitle, color, id }: PillProps) {
  const c = COLORS[color]
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={h / 2} ry={h / 2}
        fill={`url(#${id}-${color})`} stroke={c.stroke} strokeWidth={1.5} filter={`url(#${id}-sh)`} />
      <text x={x + w / 2} y={y + (subtitle ? h / 2 - 5 : h / 2 + 1)}
        textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={12} fontWeight={600} fill={c.text}>{title}</text>
      {subtitle && (
        <text x={x + w / 2} y={y + h / 2 + 10}
          textAnchor='middle' dominantBaseline='middle'
          fontFamily={F} fontSize={9.5} fill={c.text} opacity={0.6}>{subtitle}</text>
      )}
    </g>
  )
}

interface HexProps {
  x: number; y: number; w?: number; h?: number
  title: string; subtitle?: string
  color: ColorKey; id: string
}

export function Hex({ x, y, w = 200, h = 70, title, subtitle, color, id }: HexProps) {
  const c = COLORS[color]
  const indent = 20
  const pts = `${x + indent},${y} ${x + w - indent},${y} ${x + w},${y + h / 2} ${x + w - indent},${y + h} ${x + indent},${y + h} ${x},${y + h / 2}`
  return (
    <g>
      <polygon points={pts} fill={`url(#${id}-${color})`} stroke={c.stroke} strokeWidth={1.5} filter={`url(#${id}-sh)`} />
      <text x={x + w / 2} y={y + (subtitle ? h / 2 - 5 : h / 2 + 1)}
        textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={12} fontWeight={600} fill={c.text}>{title}</text>
      {subtitle && (
        <text x={x + w / 2} y={y + h / 2 + 11}
          textAnchor='middle' dominantBaseline='middle'
          fontFamily={F} fontSize={9.5} fill={c.text} opacity={0.6}>{subtitle}</text>
      )}
    </g>
  )
}

interface ArrProps {
  x1: number; y1: number; x2: number; y2: number
  label?: string; dashed?: boolean; id: string
  color?: string
}

export function Arr({ x1, y1, x2, y2, label, dashed, id, color }: ArrProps) {
  const mx = (x1 + x2) / 2
  const my = (y1 + y2) / 2
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color ?? '#94a3b8'} strokeWidth={1.5}
        strokeDasharray={dashed ? '6 4' : undefined}
        markerEnd={`url(#${id}-arr)`} />
      {label && (
        <g>
          <rect x={mx - label.length * 3.3 - 6} y={my - 9} width={label.length * 6.6 + 12} height={18}
            rx={4} fill='white' stroke='#e2e8f0' strokeWidth={0.5} />
          <text x={mx} y={my + 1} textAnchor='middle' dominantBaseline='middle'
            fontFamily={F} fontSize={9.5} fill='#64748b'>{label}</text>
        </g>
      )}
    </g>
  )
}

interface ZoneProps {
  x: number; y: number; w: number; h: number
  title: string
  bgColor?: string; borderColor?: string
  dashed?: boolean; titleColor?: string
}

export function Zone({ x, y, w, h, title, bgColor, borderColor, dashed, titleColor }: ZoneProps) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={12} ry={12}
        fill={bgColor ?? '#f8fafc'} stroke={borderColor ?? '#cbd5e1'}
        strokeWidth={1.5} strokeDasharray={dashed ? '8 4' : undefined} />
      <text x={x + 16} y={y + 20}
        fontFamily={F} fontSize={11} fontWeight={600}
        fill={titleColor ?? '#475569'} letterSpacing='0.5'>{title}</text>
    </g>
  )
}

interface BadgeProps {
  x: number; y: number; text: string; color?: string; bg?: string
}

export function Badge({ x, y, text, color = '#7c3aed', bg }: BadgeProps) {
  const w = text.length * 6.5 + 16
  return (
    <g>
      <rect x={x - w / 2} y={y - 11} width={w} height={22} rx={11}
        fill={bg ?? color} opacity={bg ? 1 : 0.12} />
      <text x={x} y={y + 1} textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={10} fontWeight={600} fill={color}>{text}</text>
    </g>
  )
}

interface StepProps {
  x: number; y: number; n: string; title: string; subtitle?: string
}

export function Step({ x, y, n, title, subtitle }: StepProps) {
  return (
    <g>
      <circle cx={x} cy={y} r={15} fill='#7c3aed' opacity={0.1} />
      <text x={x} y={y + 1} textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={12} fontWeight={700} fill='#7c3aed'>{n}</text>
      <text x={x + 26} y={y - (subtitle ? 5 : 1)} dominantBaseline='middle'
        fontFamily={F} fontSize={12} fontWeight={600} fill='#1e293b'>{title}</text>
      {subtitle && (
        <text x={x + 26} y={y + 12} dominantBaseline='middle'
          fontFamily={F} fontSize={10} fill='#64748b'>{subtitle}</text>
      )}
    </g>
  )
}

export function Label({ x, y, text, size = 11, weight = 600, color = '#475569', anchor = 'middle' as const }: {
  x: number; y: number; text: string; size?: number; weight?: number; color?: string; anchor?: 'start' | 'middle' | 'end'
}) {
  return (
    <text x={x} y={y} textAnchor={anchor} dominantBaseline='middle'
      fontFamily={F} fontSize={size} fontWeight={weight} fill={color}
      letterSpacing='0.3'>{text}</text>
  )
}

/**
 * A named part of a step-through diagram. While a step is active (`lit` set),
 * parts outside it fade and the arrows inside it animate; with no step active
 * (`lit` null) the diagram reads as a plain static picture.
 */
export function Part({ k, lit, children }: { k: string; lit?: ReadonlySet<string> | null; children: ReactNode }) {
  const on = !lit || lit.has(k)
  return (
    <g data-part={k} className={lit && on ? 'diagram-part-live' : undefined}
      style={{ opacity: on ? 1 : 0.14, transition: 'opacity 0.3s ease' }}>
      {children}
    </g>
  )
}
